import { useTranslation } from 'react-i18next';
import { format, isValid } from 'date-fns';
import { Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { AppLocales, LocaleKey } from '../../../../utils/language.util';
import { BodySmallText, BodyText, BodyXSmallText, Subhead02Text } from '../../../../components/typography/typography';
import { IAssistanceResume } from '../../../../api/assistance/assistance.types';
import { getAssistanceStatusColors } from './assistance-status';
import { getAssistanceIcon } from './assistance-icon';
import { AssistanceCardStyles } from './assistance-card-style';

type AssistanceCardProps = {
  assistance: IAssistanceResume;
  onDoSurvey: (assistance: IAssistanceResume) => void;
};

const AssistanceCard = ({ assistance, onDoSurvey }: AssistanceCardProps) => {
  const { t, i18n } = useTranslation('completed');
  const {
    fecha,
    estado,
    averia,
    colaborador,
    expediente,
    intervencion,
    ubicacion,
    servicio,
    esMobility,
    encuestaPendiente,
  } = assistance;

  const iconConfig = getAssistanceIcon(averia);
  const locale = AppLocales[(i18n.language?.slice(0, 2) as LocaleKey) ?? 'es'] ?? AppLocales.es;
  const date = new Date(fecha);
  const formattedDate = isValid(date) ? format(date, "d MMM'.' yyyy · HH:mm 'h'", { locale }) : '';

  return (
    <AssistanceCardStyles data-cy="assistance-card">
      <div className="assistance-card__header">
        <div className="assistance-card__icon" style={{ background: iconConfig.background, color: iconConfig.color }}>
          <FontAwesomeIcon icon={iconConfig.icon} />
        </div>
        <div className="assistance-card__main">
          <Subhead02Text weight={600}>{t(`assistances.fault-type.${averia}`, { defaultValue: averia })}</Subhead02Text>
          {formattedDate && <BodySmallText className="assistance-card__date">{formattedDate}</BodySmallText>}
          <BodySmallText className="assistance-card__detail">{expediente}</BodySmallText>
        </div>
      </div>
      {colaborador && (
        <BodySmallText className="assistance-card__detail">
          <FontAwesomeIcon icon={['far', 'wrench']} />
          {colaborador}
        </BodySmallText>
      )}
      {servicio && (
        <BodySmallText className="assistance-card__detail">
          <FontAwesomeIcon icon={['far', 'truck-tow']} />
          {t(`assistances.service.${servicio}`, { defaultValue: servicio })}
        </BodySmallText>
      )}
      <div className="assistance-card__footer">
        <BodyText className="assistance-card__detail assistance-card__location">
          {ubicacion && (
            <>
              <FontAwesomeIcon icon={['far', 'location-dot']} />
              {ubicacion}
            </>
          )}
        </BodyText>
        <BodyXSmallText className="assistance-card__status" style={getAssistanceStatusColors(estado)} weight={600}>
          {t(`assistances.status.${estado}`, { defaultValue: estado })}
        </BodyXSmallText>
      </div>
      {esMobility == 1 && encuestaPendiente == 0 && intervencion && (
        <div className="survey-card__button">
          <Button onClick={() => onDoSurvey(assistance)}>{t('surveys.start')}</Button>
        </div>
      )}
    </AssistanceCardStyles>
  );
};

export default AssistanceCard;
